import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Flex,
  Grid,
  Skeleton,
  Stack,
  Text,
  type BoxProps,
  type StackProps,
} from '@chakra-ui/react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronLeft, ChevronRight, Clock3, Pause, Play, Square, type LucideIcon } from 'lucide-react'
import type { ClockStatus } from '../../../api/time_clock/requests'
import { usePunch } from './hooks'
import { shiftDate } from './time'
import { clockActionItems, clockStateTone, clockTones, type ClockTone } from './presentation'
import './clock-ui.css'

export const ClockScreen = (props: StackProps) => <Stack className="aw-clock" spacing={5} minW={0} {...props} />

export const Panel = (props: BoxProps) => (
  <Box
    data-clock-enter
    bg="white"
    border="1px solid #e9e6f2"
    borderRadius="22px"
    boxShadow="0 4px 20px -12px rgba(54, 39, 96, .18)"
    p={{ base: 4, md: 6 }}
    minW={0}
    {...props}
  />
)

export function SectionHeading({
  icon: Icon,
  children,
  aside,
}: {
  icon: LucideIcon
  children: ReactNode
  aside?: ReactNode
}) {
  return (
    <Flex align="center" justify="space-between" gap={3} mb={4} wrap="wrap">
      <Flex gap={3} align="center">
        <Flex
          aria-hidden
          color="primary.100"
          bg="#f1effb"
          w="34px"
          h="34px"
          borderRadius="11px"
          align="center"
          justify="center"
        >
          <Icon size={18} strokeWidth={1.8} />
        </Flex>
        <Text as="h3" fontSize="md" fontWeight="700" letterSpacing="-.3px">
          {children}
        </Text>
      </Flex>
      {aside}
    </Flex>
  )
}

export function SummaryMetric({
  label,
  value,
  icon: Icon,
  tone = 'purple',
}: {
  label: string
  value: string | number
  icon: LucideIcon
  tone?: ClockTone
}) {
  const color = clockTones[tone]
  return (
    <Panel p={{ base: 4, md: 5 }}>
      <Flex justify="space-between" gap={3} align="start">
        <Box minW={0}>
          <Text fontSize="sm" color="solid.500" lineHeight="1.5">
            {label}
          </Text>
          <Text
            fontSize={{ base: 'xl', md: '2xl' }}
            fontWeight="700"
            letterSpacing="-.7px"
            mt={2}
            sx={{ fontVariantNumeric: 'tabular-nums' }}
          >
            {value}
          </Text>
        </Box>
        <Flex
          aria-hidden
          flexShrink={0}
          bg={color.soft}
          color={color.ink}
          w="40px"
          h="40px"
          borderRadius="13px"
          align="center"
          justify="center"
        >
          <Icon size={20} strokeWidth={1.8} />
        </Flex>
      </Flex>
    </Panel>
  )
}

export function EmptyClockState({ icon: Icon, children }: { icon: LucideIcon; children: ReactNode }) {
  return (
    <Flex direction="column" align="center" gap={3} py={6} px={4} textAlign="center">
      <Flex
        aria-hidden
        bg="#f2f0fb"
        color="primary.100"
        borderRadius="16px"
        w="48px"
        h="48px"
        align="center"
        justify="center"
      >
        <Icon size={23} strokeWidth={1.6} />
      </Flex>
      <Text color="solid.500" fontSize="sm" maxW="320px" lineHeight="1.7">
        {children}
      </Text>
    </Flex>
  )
}

export function StateBadge({ state }: { state: string }) {
  const { t } = useTranslation()
  const tone = clockTones[clockStateTone(state)]
  return (
    <Badge
      bg={tone.soft}
      color={tone.ink}
      border={`1px solid ${tone.border}`}
      display="inline-flex"
      alignItems="center"
      gap={2}
      textTransform="none"
      borderRadius="full"
      px={3}
      py={1}
      fontWeight="600"
      fontSize="xs"
      whiteSpace="nowrap"
    >
      <Box aria-hidden bg="currentColor" w="5px" h="5px" borderRadius="full" flexShrink={0} />
      {t(`timeClock.${state}`)}
    </Badge>
  )
}
export function QueryState({ loading, error, retry }: { loading: boolean; error: boolean; retry: () => unknown }) {
  const { t } = useTranslation()
  if (error)
    return (
      <Alert status="error" borderRadius="12px">
        <AlertIcon />
        <Text flex={1}>{t('timeClock.loadError')}</Text>
        <Button variant="white" size="sm" onClick={() => retry()}>
          {t('timeClock.retry')}
        </Button>
      </Alert>
    )
  if (loading)
    return (
      <Stack aria-label={t('timeClock.loading')}>
        <Skeleton height="120px" borderRadius="16px" />
        <Skeleton height="200px" borderRadius="16px" />
      </Stack>
    )
  return null
}
export function ClockActions({ status, compact = false }: { status: ClockStatus; compact?: boolean }) {
  const { t } = useTranslation()
  const punch = usePunch(status)
  return (
    <Grid
      templateColumns={compact ? 'repeat(3, minmax(0, 1fr))' : 'repeat(auto-fit, minmax(135px, 1fr))'}
      gap={2}
      w="100%"
    >
      {clockActionItems(status.allowed, compact).map(({ kind, enabled }) => {
        const ActionIcon = kind === 'out' ? Square : kind === 'pause_start' ? Pause : kind === 'in' ? Clock3 : Play
        return (
          <Button
            key={kind}
            size={compact ? 'sm' : 'lg'}
            minW={0}
            h={compact ? '44px' : '52px'}
            borderRadius="13px"
            fontSize={compact ? 'xs' : 'sm'}
            whiteSpace={compact ? 'normal' : 'nowrap'}
            px={compact ? 2 : 4}
            colorScheme={kind === 'out' ? 'red' : kind === 'pause_start' ? 'orange' : 'purple'}
            bg={kind === 'out' ? '#fff0f3' : kind === 'pause_start' ? '#fff6e3' : 'primary.100'}
            color={kind === 'out' ? '#b21634' : kind === 'pause_start' ? '#956016' : 'white'}
            border="1px solid"
            borderColor={kind === 'out' ? '#fadbe1' : kind === 'pause_start' ? '#f2ddb0' : 'transparent'}
            _hover={{ bg: kind === 'out' ? '#fadbe1' : kind === 'pause_start' ? '#f2ddb0' : 'primary.200' }}
            _disabled={{ opacity: 0.45, cursor: 'not-allowed', boxShadow: 'none' }}
            leftIcon={<ActionIcon size={compact ? 14 : 18} />}
            isLoading={punch.isPending && punch.variables === kind}
            isDisabled={punch.busy || !enabled}
            onClick={() => {
              if (enabled) punch.mutate(kind)
            }}
          >
            {t(`timeClock.${kind}`)}
          </Button>
        )
      })}
    </Grid>
  )
}
export function WeekPicker({ start, onChange }: { start: string; onChange: (start: string) => void }) {
  const { t, i18n } = useTranslation()
  const label = (date: string) =>
    new Date(`${date}T12:00:00`).toLocaleDateString(i18n.language, { day: 'numeric', month: 'short', year: 'numeric' })
  return (
    <Flex align="center" gap={2} flexWrap="wrap">
      <Button
        variant="white"
        size="sm"
        minH="40px"
        borderRadius="12px"
        aria-label={t('timeClock.previous')}
        onClick={() => onChange(shiftDate(start, -7))}
      >
        <ChevronLeft size={16} />
      </Button>
      <Text fontWeight="600" fontSize="sm" sx={{ fontVariantNumeric: 'tabular-nums' }}>
        {label(start)} – {label(shiftDate(start, 6))}
      </Text>
      <Button
        variant="white"
        size="sm"
        minH="40px"
        borderRadius="12px"
        aria-label={t('timeClock.next')}
        onClick={() => onChange(shiftDate(start, 7))}
      >
        <ChevronRight size={16} />
      </Button>
    </Flex>
  )
}
